import * as React from 'react'

import {
  ClipboardPasteIcon,
  CopyIcon,
  HouseIcon,
  InfoIcon,
  Trash2Icon,
  TruckIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph'
import StruxhubNumberWithControlsInput from '~/client/src/desktop/components/StruxhubDesktopInputs/StruxhubNumberWithControlsInput/StruxhubNumberWithControlsInput'
import PropertiesPanel from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/components/PropertiesPanel/PropertiesPanel'
import MapViewSetUpStore, {
  MapViewConfigType,
  SetUpSteps,
} from '~/client/src/desktop/views/ProjectSetUp/components/AppsSitemap/MapViewSetUp.store'
import {
  MAX_PITCH_LEVEL,
  MAX_ZOOM_LEVEL,
} from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Tooltip as ShadcnTooltip,
  TooltipContent as ShadcnTooltipContent,
  TooltipTrigger as ShadcnTooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubAttributeSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubAttributeSelector'
import { WorkflowIcon } from '~/client/src/shared/icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GlobeView from '~/client/src/shared/models/GlobeView'
import IGeoPosition from '~/client/src/shared/models/IGeoPosition'
import Sitemap from '~/client/src/shared/models/Sitemap'
import { isLocationResourceType } from '~/client/src/shared/resources/domain/locationKind'
import GlobeViewsStore from '~/client/src/shared/stores/domain/GlobeViews.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { toast } from '~/client/src/shared/utils/toast'

import ProjectSetUpPageStore from '../../views/ProjectSetUp/ProjectSetUpPage.store'
import MapEquipmentResourceCard from './MapEquipmentResourceCard'
import MapLocationResourceCard from './MapLocationResourceCard'
import RemoteTilesetProperties from './RemoteTilesetProperties'
import TilesetProperties from './TilesetProperties'

import './GlobeProperties.scss'

const maxDegrees = 180
const maxZoomValue = MAX_ZOOM_LEVEL
const minZoomValue = 0
const zoomStep = 0.01
const bearingStep = 1
const pitchStep = 1
export const maxLat = 90
export const minLat = -90

export const maxLng = 180
export const minLng = -180

const latitude = 'Latitude'
const longitude = 'Longitude'
const whiteboardProperties = 'Whiteboard properties'
const planProperties = 'Plan properties'
const viewProperties = 'View properties'
const plan = 'Plan'
const parameters = 'parameters'
const zoomText = 'Zoom'
const rotationText = 'Rotation'
const pitch = 'Pitch'
const showViewIn = 'Show view in'
const updateGeoreferencing = 'Update Georeferencing'
const updatePlanImage = 'Update Plan Image'
const copyValues = 'Copy values'
const pasteValues = 'Paste values'
const DUPLICATE_BUTTON_CLASSES =
  'no-grow mx10 tw:flex tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-muted-foreground'

interface IProps {
  store: MapViewSetUpStore
  projectSetUpPageStore: ProjectSetUpPageStore

  globe: GlobeView
  updateGlobeView: (
    geoposition?: IGeoPosition,
    bounds?,
    items?,
    sitemaps?,
  ) => void

  isItemSelected: boolean
  selectedSitemap?: Sitemap

  saveAlignment: () => void
  exitRubber: () => void

  isGlobeMode?: boolean
  state?: InitialState
  sitemapsStore?: SitemapsStore
  globeViewsStore?: GlobeViewsStore
  isDisabled?: boolean
}

// TODO: extract all the text to the top
@inject('state', 'sitemapsStore', 'globeViewsStore')
@observer
export default class GlobeProperties extends React.Component<IProps> {
  private get viewport(): IGeoPosition {
    return this.props.store.mapBoxViewerStore.viewport
  }

  // The map object currently selected is a location → show the consolidated
  // resource card instead of PropertiesPanel. Locations and equipment each have
  // one; sensors, materials and integrations do not, which is exactly what
  // isLocationResourceType excludes.
  private get isLocationItemSelected(): boolean {
    const dataObject =
      this.props.store.mapViewItemsSetupStore.selectedMapViewItem?.dataObject
    return this.props.isItemSelected && isLocationResourceType(dataObject?.type)
  }

  // Either resource card takes the wider panel.
  private get isResourceItemSelected(): boolean {
    return this.isEquipmentItemSelected || this.isLocationItemSelected
  }

  private get isEquipmentItemSelected(): boolean {
    const dataObject =
      this.props.store.mapViewItemsSetupStore.selectedMapViewItem?.dataObject
    // Both existing and freshly-drawn equipment use the resource card; the card
    // renders in creation mode when the drawn item has no backend id yet.
    return (
      this.props.isItemSelected &&
      dataObject?.type === LocationType.OffloadingEquipment
    )
  }

  public render(): JSX.Element {
    const { isGlobeMode, isDisabled } = this.props

    return (
      <div
        className={classList({
          'globe-properties-panel full-height relative': true,
          'globe-panel': isGlobeMode,
          'inactive-element': isDisabled,
        })}
        // The resource card matches the equipment side panel width (300 → 400).
        style={
          this.isResourceItemSelected
            ? { flexBasis: 400, minWidth: 400, maxWidth: 400 }
            : undefined
        }
      >
        {this.renderPanel()}
      </div>
    )
  }

  private renderPanel(): JSX.Element {
    const {
      isItemSelected,
      selectedSitemap,
      saveAlignment,
      exitRubber,
      store: { mapBoxEditorStore },
      projectSetUpPageStore,
    } = this.props

    switch (true) {
      case this.isLocationItemSelected:
        return (
          <MapLocationResourceCard
            // Remount all record-owned state when the selected location changes
            // (and when a drawn item transitions from unsaved to saved). An unsaved item
            // falls back to its own transientKey rather than a shared literal, so two drawn
            // items are never the same React subtree - otherwise the second would inherit
            // the first's form draft.
            key={
              this.props.store.mapViewItemsSetupStore.selectedMapViewItem?.id ??
              this.props.store.mapViewItemsSetupStore.selectedMapViewItem
                ?.transientKey ??
              'new-location'
            }
            store={this.props.store}
            projectSetUpPageStore={projectSetUpPageStore}
            viewport={this.viewport}
            saveAlignment={saveAlignment}
            exitRubber={exitRubber}
            projectId={this.props.state?.activeProject?.id}
          />
        )
      case this.isEquipmentItemSelected:
        return (
          <MapEquipmentResourceCard
            // Remount all record-owned state when the selected equipment changes
            // (and when a drawn item transitions from unsaved to saved).
            key={
              this.props.store.mapViewItemsSetupStore.selectedMapViewItem?.id ??
              'new-equipment'
            }
            store={this.props.store}
            projectSetUpPageStore={projectSetUpPageStore}
            viewport={this.viewport}
            saveAlignment={saveAlignment}
            exitRubber={exitRubber}
            projectId={this.props.state?.activeProject?.id}
          />
        )
      case isItemSelected ||
        (selectedSitemap?.id && mapBoxEditorStore.isRubberMode):
        return (
          <PropertiesPanel
            store={this.props.store}
            step={SetUpSteps.alignPlan}
            saveAlignment={saveAlignment}
            exitRubber={exitRubber}
            viewport={this.viewport}
            projectSetUpPageStore={projectSetUpPageStore}
          />
        )
      case !this.props.state.isTilesetsDisabled &&
        (!!this.props.store.mapBoxViewerStore.tilesetsControlStore
          .selectedTilesetFeature ||
          !!this.props.store.tilesetsSetupStore.selectedTileset):
        return <TilesetProperties store={this.props.store} />
      case !this.props.state.isTilesetsDisabled &&
        !!this.props.store.remoteTilesetsSetupStore.selectedRemoteTileset:
        return <RemoteTilesetProperties store={this.props.store} />
      default:
        return (
          <div className="full-height scrollable">{this.renderContent()}</div>
        )
    }
  }

  private renderContent(): JSX.Element {
    const { store, selectedSitemap } = this.props

    if (selectedSitemap?.id) {
      return (
        <div className="col bb-light-cool-grey">
          <div className="col py10">
            <div className="px10 row pb12 bb-light-input-border">
              <div className="text header bold center">
                {selectedSitemap?.isReferenced
                  ? planProperties
                  : whiteboardProperties}
              </div>
            </div>
            {this.renderHeader()}
            <div className="px10">
              <StruxhubInput
                label={Localization.translator.name}
                value={store.sitemapsSetupStore.editableSitemapName}
                isRequired={true}
                isRequiredTextHidden={true}
                isMinimalisticMode={false}
                onChange={this.changeSitemapName}
                withDelay={true}
              />
            </div>
          </div>
          {this.renderLBSTag()}
          {selectedSitemap?.isReferenced
            ? this.renderPlanProperties()
            : this.renderShowView()}
        </div>
      )
    }

    return (
      <div className="col">
        <div className="col py10">
          <div className="row pb12 bb-light-input-border">
            <div className="text header bold center">{viewProperties}</div>
          </div>
          <div className="px10">
            <StruxhubInput
              label={Localization.translator.name}
              value={store.globeViewSetupStore.editableGlobeName}
              onChange={this.changeGlobeName}
              withDelay={true}
              isRequired={true}
              isRequiredTextHidden={true}
              isMinimalisticMode={false}
            />
          </div>
          {this.renderLBSTag()}
        </div>
        {this.renderViewOrientation()}
        {this.renderShowView()}
      </div>
    )
  }

  private renderPlanProperties(): JSX.Element {
    const { sitemapsStore, selectedSitemap } = this.props

    return (
      <>
        {this.renderPlanOrientation()}
        <>
          <div
            className="pa10 text large blue center pointer"
            onClick={this.onSitemapGeoEditClick.bind(
              null,
              sitemapsStore.byId.get(selectedSitemap.id),
            )}
          >
            {updateGeoreferencing}
          </div>
          <div
            className="pa10 text large blue center pointer bb-light-cool-grey"
            onClick={this.props.store.sitemapControlStore.toggleEditMenu}
          >
            {updatePlanImage}
          </div>
        </>
        {this.renderViewOrientation()}
      </>
    )
  }

  private renderPlanOrientation(): JSX.Element {
    const { selectedSitemap } = this.props
    const label = `${plan} ${parameters}`
    return (
      <div className="col pa10 bb-light-cool-grey">
        <div className="row">
          <div className="text large pb12 bold">{label}</div>
        </div>
        <div className="relative duplicate-input">
          <StruxhubInput
            label={latitude}
            type="number"
            isReadOnly={true}
            value={selectedSitemap.center.lat.toFixed(2)}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            negativeOrDecimal={true}
          />
        </div>
        <div className="relative duplicate-input">
          <StruxhubInput
            label={longitude}
            type="number"
            isReadOnly={true}
            value={selectedSitemap.center.lng.toFixed(2)}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            negativeOrDecimal={true}
          />
        </div>
        <div className="relative duplicate-input">
          <StruxhubInput
            label={zoomText}
            type="number"
            isReadOnly={true}
            value={selectedSitemap.zoom?.toFixed(2)}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            negativeOrDecimal={true}
          />
        </div>
        <div className="relative duplicate-input">
          <StruxhubInput
            label={rotationText}
            type="number"
            isReadOnly={true}
            value={selectedSitemap.bearing?.toFixed(2)}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            negativeOrDecimal={true}
          />
        </div>
      </div>
    )
  }

  private renderViewOrientation(): JSX.Element {
    const viewport = this.viewport
    const label = `${Localization.translator.view_noun} ${parameters}`
    return (
      <div className="col pa10 bb-light-cool-grey">
        <div className="row">
          <div className="text large pb12 bold">{label}</div>
          {this.renderDuplicate()}
        </div>
        <div className="relative duplicate-input">
          <StruxhubInput
            label={latitude}
            type="number"
            isRequired={true}
            value={viewport.latitude.toString()}
            onChange={this.changeLat}
            negativeOrDecimal={true}
            isRequiredTextHidden={true}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            withDelay={true}
            min={minLat}
            max={maxLat}
          />
        </div>
        <div className="relative duplicate-input">
          <StruxhubInput
            label={longitude}
            type="number"
            isRequired={true}
            value={viewport.longitude.toString()}
            onChange={this.changeLng}
            negativeOrDecimal={true}
            isRequiredTextHidden={true}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            withDelay={true}
            min={minLng}
            max={maxLng}
          />
        </div>
        <div className="relative duplicate-input">
          <StruxhubNumberWithControlsInput
            label={zoomText}
            type="number"
            isRequired={true}
            max={maxZoomValue}
            min={minZoomValue}
            step={zoomStep}
            value={viewport.zoom.toFixed(3)}
            onChange={this.changeZoom}
            onValueChange={this.changeZoomValue}
            negativeOrDecimal={true}
            isRequiredTextHidden={true}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            withDelay={true}
          />
          <ShadcnTooltip>
            <ShadcnTooltipTrigger
              type="button"
              className="tw:absolute tw:top-[10px] tw:left-[42px] tw:flex tw:items-center tw:justify-center tw:size-4 tw:border-0 tw:bg-transparent tw:cursor-default"
            >
              <InfoIcon size={16} className="tw:text-muted-foreground" />
            </ShadcnTooltipTrigger>
            <ShadcnTooltipContent side="top">
              {Localization.translator.zoomOverviewReportTooltip}
            </ShadcnTooltipContent>
          </ShadcnTooltip>
        </div>
        <div className="relative duplicate-input">
          <StruxhubNumberWithControlsInput
            label={rotationText}
            type="number"
            isRequired={true}
            max={maxDegrees}
            min={-maxDegrees}
            step={bearingStep}
            value={viewport.bearing.toFixed(2)}
            onChange={this.changeBearing}
            onValueChange={this.changeBearingValue}
            negativeOrDecimal={true}
            isRequiredTextHidden={true}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            withDelay={true}
          />
        </div>
        <div className="relative duplicate-input">
          <StruxhubNumberWithControlsInput
            label={pitch}
            type="number"
            step={pitchStep}
            isRequired={true}
            value={viewport.pitch.toFixed(2)}
            onChange={this.changePitch}
            onValueChange={this.changePitchValue}
            negativeOrDecimal={true}
            isRequiredTextHidden={true}
            shouldLineUpLabel={true}
            isMinimalisticMode={false}
            hideBottomLabel={true}
            withDelay={true}
            min={0}
            max={MAX_PITCH_LEVEL}
          />
        </div>
      </div>
    )
  }

  private renderShowView(): JSX.Element {
    const { selectedSitemap, state, store } = this.props
    const { isDeliveriesDisabled, isWorkflowsDisabled, isLogisticsDisabled } =
      state
    const {
      logisticsAssignedGlobes,
      formsAssignedGlobes,
      deliveriesAssignedGlobes,
      logisticsAssignedSitemaps,
      formsAssignedSitemaps,
      deliveriesAssignedSitemaps,
      globeViewSetupStore: { selectedGlobeViewId, selectedGlobeView },
      onGlobeSectionClick,
      onWhiteboardSectionClick,
      isGlobeMode,
    } = store

    return (
      <div className="col view-select-globe-props pa10">
        <div className="text large">{showViewIn}</div>
        <div className="pa10">
          {!isLogisticsDisabled && (
            <div className="row mt10 y-center">
              <Checkbox
                checked={
                  isGlobeMode
                    ? logisticsAssignedGlobes[selectedGlobeViewId]
                    : logisticsAssignedSitemaps[selectedSitemap?.id]
                }
                onCheckedChange={() =>
                  isGlobeMode
                    ? onGlobeSectionClick(
                        selectedGlobeView,
                        MapViewConfigType.logistics,
                      )
                    : onWhiteboardSectionClick(
                        selectedSitemap,
                        MapViewConfigType.logistics,
                      )
                }
              />
              <div className="bg-light-cool-grey brada4 row no-grow px5">
                <HouseIcon className="tw:mr-1.5 tw:size-4 tw:flex-none! tw:text-foreground" />
                <div className="text">{Localization.translator.home}</div>
              </div>
            </div>
          )}
          {!isWorkflowsDisabled && (
            <div className="row mt10 y-center">
              <Checkbox
                checked={
                  isGlobeMode
                    ? formsAssignedGlobes[selectedGlobeViewId]
                    : formsAssignedSitemaps[selectedSitemap?.id]
                }
                onCheckedChange={() =>
                  isGlobeMode
                    ? onGlobeSectionClick(
                        selectedGlobeView,
                        MapViewConfigType.forms,
                      )
                    : onWhiteboardSectionClick(
                        selectedSitemap,
                        MapViewConfigType.forms,
                      )
                }
              />
              <div className="bg-light-cool-grey brada4 row no-grow px5">
                <WorkflowIcon className="tw:mr-1.5 tw:size-4 tw:flex-none! tw:text-foreground" />
                <div className="text">{Localization.translator.workflows}</div>
              </div>
            </div>
          )}
          {!isDeliveriesDisabled && (
            <div className="row text mt10 y-center">
              <Checkbox
                checked={
                  isGlobeMode
                    ? deliveriesAssignedGlobes[selectedGlobeViewId]
                    : deliveriesAssignedSitemaps[selectedSitemap?.id]
                }
                onCheckedChange={() =>
                  isGlobeMode
                    ? onGlobeSectionClick(
                        selectedGlobeView,
                        MapViewConfigType.deliveries,
                      )
                    : onWhiteboardSectionClick(
                        selectedSitemap,
                        MapViewConfigType.deliveries,
                      )
                }
              />
              <div className="bg-light-cool-grey brada4 row no-grow px5">
                <TruckIcon className="tw:mr-1.5 tw:size-4 tw:flex-none! tw:text-foreground" />
                <div className="text">{Localization.translator.deliveries}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  private renderLBSTag(): JSX.Element {
    const { store, selectedSitemap, globeViewsStore } = this.props
    const globe = store.globeViewSetupStore.selectedGlobeView
    if (!selectedSitemap?.id && !globe) return null

    const assignedId = selectedSitemap?.id
      ? store.mapViewItemsSetupStore.sitemapAssignedItems(selectedSitemap)[0]
          ?.id
      : globeViewsStore.globeAssignedLocations(globe)[0]?.id

    return (
      <div className="tw:px-2.5" onClickCapture={this.setLbsTagAnchor}>
        <StruxhubAttributeSelector
          label={Localization.translator.lbsTag}
          labelTooltip={Localization.translator.lbsTagTooltip}
          isRequiredTextHidden={true}
          value={assignedId ?? ''}
          onClick={this.toggleLbsTagPicker}
        />
      </div>
    )
  }

  // The popover opens beside the field that was pressed.
  private setLbsTagAnchor = (event: React.MouseEvent<HTMLElement>): void => {
    const { store, selectedSitemap } = this.props
    if (selectedSitemap?.id) {
      store.sitemapsSetupStore.setAssignDialogAnchor(event.currentTarget)
    } else {
      store.globeViewSetupStore.setAssignDialogAnchor(event.currentTarget)
    }
  }

  private toggleLbsTagPicker = (): void => {
    const { store, selectedSitemap } = this.props
    if (selectedSitemap?.id) {
      store.sitemapsSetupStore.toggleAssignSitemapDialog()
    } else {
      store.globeViewSetupStore.toggleAssignGlobeDialog()
    }
  }

  private renderHeader(): JSX.Element {
    return (
      <div className="tw:flex tw:justify-end tw:p-2.5">
        <Button
          variant="destructive"
          size="icon"
          aria-label={Localization.translator.delete}
          onClick={this.deleteSitePlan}
        >
          <Trash2Icon />
        </Button>
      </div>
    )
  }

  private renderDuplicate = (): JSX.Element => {
    return (
      <>
        <ShadcnTooltip>
          <ShadcnTooltipTrigger
            type="button"
            aria-label={copyValues}
            className={DUPLICATE_BUTTON_CLASSES}
            onClick={this.copyText}
          >
            <CopyIcon size={16} />
          </ShadcnTooltipTrigger>
          <ShadcnTooltipContent side="bottom">
            {copyValues}
          </ShadcnTooltipContent>
        </ShadcnTooltip>
        <ShadcnTooltip>
          <ShadcnTooltipTrigger
            type="button"
            aria-label={pasteValues}
            className={DUPLICATE_BUTTON_CLASSES}
            onClick={this.pasteText}
          >
            <ClipboardPasteIcon size={16} />
          </ShadcnTooltipTrigger>
          <ShadcnTooltipContent side="bottom">
            {pasteValues}
          </ShadcnTooltipContent>
        </ShadcnTooltip>
      </>
    )
  }

  private deleteSitePlan = (): void => {
    this.props.store.sitemapControlStore.showDeleteDialog()
  }

  private onSitemapGeoEditClick = (sitemap: Sitemap): void => {
    const { store } = this.props
    store.sitemapsSetupStore.selectSitemap(sitemap, false)
    store.setStep(SetUpSteps.alignPlan)
    store.mapBoxViewerStore.setViewportFromAddress(sitemap, true)
    store.mapBoxEditorStore.setLeftVisibility()
  }

  private copyText = async (): Promise<void> => {
    const { longitude, latitude, zoom, bearing, pitch } = this.viewport
    const text = `${longitude};${latitude};${zoom};${bearing};${pitch}`
    await navigator.clipboard
      .writeText(text)
      .then(() => toast.success(Localization.translator.copiedToClipboard))
  }

  private pasteText = async (): Promise<void> => {
    await navigator.clipboard.readText().then(result => {
      const [lng, lat, zoom, bearing, pitch] = result.split(';')
      if (
        !lng ||
        isNaN(Number(lng)) ||
        !lat ||
        isNaN(Number(lat)) ||
        !zoom ||
        isNaN(Number(zoom)) ||
        !bearing ||
        isNaN(Number(bearing)) ||
        !pitch ||
        isNaN(Number(pitch))
      ) {
        toast.error('Wrong input')
      } else {
        const {
          store: { mapBoxViewerStore },
        } = this.props
        mapBoxViewerStore.jumpToViewport({
          latitude: Number(lat),
          longitude: Number(lng),
          zoom: Number(zoom),
          bearing: Number(bearing),
          pitch: Number(pitch),
        })
        toast.success(Localization.translator.successfullyUpdated)
      }
    })
  }

  private changeGlobeName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { globeViewSetupStore } = this.props.store
    globeViewSetupStore.changeName(event)
    globeViewSetupStore.saveGlobeViewName()
  }

  private changeSitemapName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { sitemapsSetupStore } = this.props.store
    sitemapsSetupStore.updateEditableSitemapName(event.target.value)
    sitemapsSetupStore.updateSelectedSitemapName()
  }

  private changeLng = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.props.store.mapBoxViewerStore.setViewportProp(
      Number(event.target.value),
      'longitude',
    )
  }

  private changeLat = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.props.store.mapBoxViewerStore.setViewportProp(
      Number(event.target.value),
      'latitude',
    )
  }

  private changePitchValue = (value: number): void => {
    this.props.store.mapBoxViewerStore.setViewportProp(value, 'pitch')
  }

  private changePitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.changePitchValue(Number(event.target.value))
  }

  private changeZoom = (event: React.ChangeEvent<HTMLInputElement>): void => {
    this.changeZoomValue(Number(event.target.value))
  }

  private changeZoomValue = (value: number): void => {
    this.props.store.mapBoxViewerStore.setViewportProp(value, 'zoom')
  }

  private changeBearing = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.changeBearingValue(Number(event.target.value))
  }

  private changeBearingValue = (value: number): void => {
    this.props.store.mapBoxViewerStore.setViewportProp(value, 'bearing')
  }
}
